import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  redirectTo?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requireAuth = true, 
  redirectTo = '/login' 
}) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Afficher un loader pendant la vérification de l'authentification
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
            <img
              src="/images/casap192.png"
              alt="CasaPresta"
              className="w-16 h-16 rounded-full object-cover"
              onError={(e) => {
                const target = e.currentTarget;
                const nextElement = target.nextElementSibling as HTMLElement;
                target.style.display = 'none';
                if (nextElement) {
                  nextElement.style.display = 'flex';
                }
              }}
            />
            <span className="text-white font-bold text-xl hidden">CasaPresta</span>
          </div>
          <h2 className="text-xl font-script font-bold text-primary-900 mb-2">CasaPresta</h2>
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
            <span className="ml-2 text-sm text-gray-500">Vérification...</span>
          </div>
        </div>
      </div>
    );
  }

  // Si l'authentification est requise mais l'utilisateur n'est pas connecté
  if (requireAuth && !user) {
    // Sauvegarder la page demandée pour rediriger après connexion
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Si l'utilisateur est connecté mais ne devrait pas accéder à cette page (ex: login quand déjà connecté)
  // On vérifie s'il est provider avant de rediriger
  if (!requireAuth && user) {
    // Check if this is a provider session
    const isProviderSession = localStorage.getItem('provider_session') === 'true';
    if (isProviderSession) {
      // Provider already logged in, allow access to children (login/register pages)
      return <>{children}</>;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;